import { useEffect, useState, useCallback } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LabelList } from "recharts";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { FiltersBar, Kpi, Loading, CARD, LBL, fmtAht, fmtNum, fmtDelta, fq } from "./axiom";

const barColor = (aht, avg) => (aht > avg + 30 ? "#F43F5E" : aht > avg ? "#F59E0B" : "#10B981");
const fmtDate = (d) => new Date(d + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short" });

export const DateTab = ({ summary, filters, setFilters }) => {
  const [data, setData] = useState(null);
  const [drill, setDrill] = useState(null);
  const [window_, setWindow] = useState(30);

  const load = useCallback(() => { setDrill(null); api.get(`/aht/date-analysis?${fq(filters)}`).then((r) => setData(r.data)); }, [filters]);
  useEffect(() => { load(); }, [load]);

  const openDrill = async (date) => {
    const { data: d } = await api.get(`/aht/date-hourly?date=${date}&${fq(filters)}`);
    setDrill(d);
  };

  if (!data) return <Loading />;
  if (!data.rows.length) return <p className="py-20 text-center text-sm text-thdim">No dated data — upload files with a Created/Date column.</p>;

  const rows = data.rows.slice(-window_);
  const k = data.kpis;
  const dates = data.rows.map((r) => r.date);
  const drillIdx = drill ? dates.indexOf(drill.date) : -1;

  return (
    <div>
      <h2 className="font-heading mb-5 text-xl font-bold text-thstrong">Date-wise AHT Analysis</h2>
      <FiltersBar summary={summary} filters={filters} setFilters={setFilters} />

      <div className="mb-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi testId="aht-date-worst" label="Worst Day" value={<span className="rounded bg-red-500 px-2 py-0.5 text-white">{fmtAht(k.worst.aht)}</span>}
          sub={`${fmtDate(k.worst.date)} · ${fmtNum(k.worst.calls)} calls · ${fmtDelta(k.worst.vs_avg)} vs avg`} border="#F43F5E" />
        <Kpi testId="aht-date-best" label="Best Day" value={<span className="rounded bg-thmint px-2 py-0.5 text-white">{fmtAht(k.best.aht)}</span>}
          sub={`${fmtDate(k.best.date)} · ${fmtNum(k.best.calls)} calls · ${fmtDelta(k.best.vs_avg)} vs avg`} border="#10B981" />
        <Kpi testId="aht-date-volatility" label="Daily AHT Volatility" value={`±${Math.round(k.volatility.std)}s`} accent="#F59E0B"
          sub={`Std dev across ${k.volatility.days} days · CV ${k.volatility.cv.toFixed(1)}%`} border="#F59E0B" />
        <Kpi testId="aht-date-weekend" label="Weekend vs Weekday" accent="#3b82f6"
          value={fmtDelta((k.weekend || 0) - (k.weekday || 0))}
          sub={`Wknd: ${fmtAht(k.weekend)} · Wkdy: ${fmtAht(k.weekday)}`} border="#3b82f6" />
      </div>

      <div className={`${CARD} mb-6 p-6`}>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm font-bold text-thstrong">Daily AHT <span className="font-normal text-thdim">· Last {window_} days</span></p>
          <div className="flex items-center gap-4">
            <select data-testid="aht-date-window" value={window_} onChange={(e) => setWindow(Number(e.target.value))}
              className="rounded-lg border border-thborder/12 px-2 py-1.5 text-xs">
              {[14, 30, 60, 90].map((w) => <option key={w} value={w}>Last {w} Days</option>)}
            </select>
            <span className="flex items-center gap-3 text-[11px] text-thmute">
              <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-thmint" /> Below Avg</span>
              <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-amber-500" /> Above Avg</span>
              <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-red-500" /> High (+30s)</span>
            </span>
          </div>
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} onClick={(e) => e?.activeLabel && openDrill(e.activeLabel)}>
              <XAxis dataKey="date" tickFormatter={(d) => fmtDate(d)} tick={{ fill: "#7a8fb5", fontSize: 8, fontFamily: "JetBrains Mono" }} angle={-40} textAnchor="end" height={55} interval={0} />
              <YAxis tick={{ fill: "#7a8fb5", fontSize: 9, fontFamily: "JetBrains Mono" }} tickFormatter={(v) => `${v}s`} />
              <Tooltip formatter={(v) => fmtAht(v)} labelFormatter={fmtDate} contentStyle={{ background: "rgb(var(--th-solid))", border: "1px solid rgb(var(--th-border) / 0.14)", borderRadius: 10, fontSize: 11, color: "rgb(var(--th-strong))" }} />
              <Bar dataKey="aht" cursor="pointer" radius={[2, 2, 0, 0]}>
                <LabelList dataKey="aht" position="top" formatter={(v) => fmtAht(v)} style={{ fontSize: 8, fill: "#7a8fb5", fontFamily: "JetBrains Mono" }} />
                {rows.map((r) => <Cell key={r.date} fill={barColor(r.aht, data.avg)} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <p className="mt-2 text-[11px] text-thdim">Click any bar for hourly breakdown · overall avg {fmtAht(data.avg)}</p>
      </div>

      {drill && (
        <div data-testid="aht-hourly-drill" className={`${CARD} border-amber-300 p-6`} style={{ borderColor: "rgb(var(--th-amber) / 0.6)" }}>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm font-bold text-thstrong">
              ⏱ Hourly AHT for <span className="text-amber-600">{fmtDate(drill.date)}</span>
              <span className="ml-2 font-normal text-thdim">· {fmtNum(drill.calls)} calls · Avg {fmtAht(drill.avg)}</span>
            </p>
            <div className="flex items-center gap-2">
              <button data-testid="aht-drill-prev" disabled={drillIdx <= 0} onClick={() => openDrill(dates[drillIdx - 1])}
                className="rounded-lg border border-thborder/12 p-1.5 text-thmute disabled:opacity-30"><ChevronLeft className="h-4 w-4" /></button>
              <span className="font-mono-hud rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-700">{drill.date}</span>
              <button data-testid="aht-drill-next" disabled={drillIdx >= dates.length - 1} onClick={() => openDrill(dates[drillIdx + 1])}
                className="rounded-lg border border-thborder/12 p-1.5 text-thmute disabled:opacity-30"><ChevronRight className="h-4 w-4" /></button>
              <button data-testid="aht-drill-close" onClick={() => setDrill(null)}
                className="ml-2 flex items-center gap-1 rounded-lg border border-thborder/12 px-2.5 py-1.5 text-xs text-thmute hover:text-red-500"><X className="h-3.5 w-3.5" /> Close</button>
            </div>
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={drill.rows}>
                <XAxis dataKey="hour" tickFormatter={(h) => `${String(h).padStart(2, "0")}:00`} tick={{ fill: "#7a8fb5", fontSize: 9, fontFamily: "JetBrains Mono" }} />
                <YAxis tick={{ fill: "#7a8fb5", fontSize: 9, fontFamily: "JetBrains Mono" }} tickFormatter={(v) => `${v}s`} />
                <Tooltip formatter={(v) => fmtAht(v)} labelFormatter={(h) => `${String(h).padStart(2, "0")}:00`} contentStyle={{ background: "rgb(var(--th-solid))", border: "1px solid rgb(var(--th-border) / 0.14)", borderRadius: 10, fontSize: 11, color: "rgb(var(--th-strong))" }} />
                <Bar dataKey="aht" radius={[2, 2, 0, 0]}>
                  <LabelList dataKey="aht" position="top" formatter={(v) => fmtAht(v)} style={{ fontSize: 8, fill: "#7a8fb5", fontFamily: "JetBrains Mono" }} />
                  {drill.rows.map((r) => <Cell key={r.hour} fill={barColor(r.aht, drill.avg)} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <p className={`${LBL} mt-2`}>Hours with volume on this date · colors vs day average</p>
        </div>
      )}
    </div>
  );
};
